import {AsyncPipe, DatePipe, Location, NgTemplateOutlet} from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {IPatientRelation} from '@app/checkout/models/patient-relation.model';
import {ISampleType} from '@app/checkout/models/sample-type.model';
import {getPatientRelationTypes, getSampleTypesFromOrderDetailsPage} from '@app/checkout/state/checkout.actions';
import {selectPatientRelationTypes, selectSampleTypes} from '@app/checkout/state/checkout.selector';
import {EClinicalHistoryFields} from '@app/order/models/clinical-history.model';
import {IEditOrderState, IOrderResultsPdfState} from '@app/order/models/my-order-state.model';
import {DateFormat, DateTimeFormat} from '@app/shared/constants';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {EDynamicFormFieldType} from '@app/shared/models/dynamic.model';
import {DynamicFormUtils} from '@app/shared/utils/dynamic-form.utils';
import {Store} from '@ngrx/store';
import {TranslatePipe} from '@ngx-translate/core';
import {PrimeTemplate} from 'primeng/api';
import {Bind} from 'primeng/bind';
import {Button} from 'primeng/button';
import {Divider} from 'primeng/divider';
import {Panel} from 'primeng/panel';
import {ProgressSpinner} from 'primeng/progressspinner';
import {Tag} from 'primeng/tag';
import {Observable, Subject, takeUntil, tap} from 'rxjs';
import {
  downloadOrderResults,
  getEditOrderDetails,
  getOrderResultsPdfDetails,
  resetDownloadOrderResultsStatus,
  resetEditOrderDetails,
  resetOrderResultsPdfDetails,
} from '../state/my-order.actions';
import {
  selectDownloadOrderResultsStatus,
  selectEditOrder,
  selectEditOrderStatus,
  selectOrderResultsPdfInfo,
} from '../state/my-order.selector';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss'],
  imports: [
    Bind,
    ProgressSpinner,
    Tag,
    Button,
    Panel,
    PrimeTemplate,
    NgTemplateOutlet,
    Divider,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class OrderDetailsComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  editOrderDetails$: Observable<IEditOrderState | null>;
  eDynamicFormType = EDynamicFormFieldType;
  eClinicalHistoryFields = EClinicalHistoryFields;
  orderResultsPdfInfo$: Observable<IOrderResultsPdfState[] | null>;
  dynamicFormUtils = DynamicFormUtils;
  dateFormat = DateFormat;
  dateFormatWithTime = DateTimeFormat;
  days = 1;
  orderId: string | null = null;
  downloadInProgress = false;
  private readonly unsubscribe$ = new Subject<void>();

  sampleTypes$: Observable<ISampleType[]>;
  patientRelationTypes$: Observable<IPatientRelation[]>;
  activePatientRelation = true;
  hpoFirstPartition = 5;
  hpoSecondPartition = 10;
  editOrderInfoLoadingProgress = false;
  constructor() {
    this.store.dispatch(resetEditOrderDetails());
    this.editOrderDetails$ = this.store.select(selectEditOrder);
    this.orderResultsPdfInfo$ = this.store.select(selectOrderResultsPdfInfo);
    this.sampleTypes$ = this.store.select(selectSampleTypes);
    this.patientRelationTypes$ = this.store.select(selectPatientRelationTypes);

    this.store
      .select(selectDownloadOrderResultsStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.downloadInProgress = false;
          this.store.dispatch(resetDownloadOrderResultsStatus());
        })
      )
      .subscribe();
    this.store
      .select(selectEditOrderStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((status: EActionStatus) => {
          if (status === EActionStatus.Success || status === EActionStatus.Failure) {
            this.editOrderInfoLoadingProgress = false;
          }
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.store.dispatch(getSampleTypesFromOrderDetailsPage());
    this.store.dispatch(getPatientRelationTypes({enabled: this.activePatientRelation}));
    this.getEditOrderDetails();
    this.store.dispatch(resetOrderResultsPdfDetails());
    this.getOrderResultsPdfDetails();
    this.orderId = this.getOrderId();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  backToOrders(): void {
    if (window.history.length <= 1) this.router.navigate(['/my-orders']);
    else this.location.back();
  }

  downloadOrderResults(fileName: string): void {
    const orderId = this.getOrderId();
    if (orderId) {
      this.downloadInProgress = true;
      this.store.dispatch(downloadOrderResults({orderId: orderId, name: fileName}));
    }
  }

  private getEditOrderDetails(): void {
    const orderId = this.getOrderId();
    if (orderId) {
      this.editOrderInfoLoadingProgress = true;
      this.store.dispatch(
        getEditOrderDetails({
          orderId: orderId,
        })
      );
    }
  }

  private getOrderResultsPdfDetails(): void {
    const orderId = this.getOrderId();
    if (orderId) {
      this.store.dispatch(
        getOrderResultsPdfDetails({
          orderId: orderId,
        })
      );
    }
  }

  private getOrderId(): string | null {
    return this.route.snapshot.queryParamMap?.get('orderId') ? this.route.snapshot.queryParamMap?.get('orderId') : null;
  }

  getOrderStatusStyle(statusId?: number | null): string {
    if (statusId) {
      if (statusId === OrderStatusEnum.InProgress) {
        return 'p-tag-info';
      } else if (statusId === OrderStatusEnum.Confirmed) {
        return 'p-tag-confirmed';
      } else if (statusId === OrderStatusEnum.Completed) {
        return 'p-tag-result';
      } else if (statusId === OrderStatusEnum.Cancelled) {
        return 'p-tag-inactive';
      } else if (statusId === OrderStatusEnum.New) {
        return 'p-tag-new';
      }
    }
    return 'p-tag-draft';
  }

  getSampleTypeLabel(sampleTypes: ISampleType[], sampleTypeId: number): string | undefined {
    return sampleTypes.find(sampleType => sampleType.id === sampleTypeId)?.label;
  }

  getPatientRelationLabel(patientRelations: IPatientRelation[], patientRelationTypeId: number): string | undefined {
    return patientRelations.find(patientRelation => patientRelation.id === patientRelationTypeId)?.name;
  }
}
